import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Pause, Play } from "lucide-react";

/**
 * Year-round calming decoration: crescent moons that glow and drift slowly,
 * and feathers that fall gently. Each icon links to an existing calming page.
 * Icons stay in the side gutters so they never sit over main content, are
 * pausable, and become still for anyone who prefers reduced motion.
 */

type Item = {
  kind: "moon" | "feather";
  side: "left" | "right";
  inset: number; // % from the side
  size: number;
  delay: number;
  duration: number;
  top?: number; // moons only, % from top
  tone: string;
  to: "/sleep" | "/meditations" | "/sounds" | "/breathing" | "/grounding" | "/affirmations";
  label: string;
};

const ITEMS: Item[] = [
  { kind: "moon", side: "left", inset: 2, top: 22, size: 26, delay: 0, duration: 18, tone: "var(--calm-gold)", to: "/sleep", label: "Open sleep support" },
  { kind: "moon", side: "right", inset: 2.5, top: 48, size: 22, delay: 6, duration: 22, tone: "var(--calm-gold-warm)", to: "/meditations", label: "Open meditations" },
  { kind: "moon", side: "left", inset: 3, top: 72, size: 20, delay: 11, duration: 20, tone: "var(--calm-gold-soft)", to: "/sounds", label: "Open calming sounds" },
  { kind: "feather", side: "right", inset: 1.5, size: 24, delay: 0, duration: 32, tone: "var(--calm-white)", to: "/breathing", label: "Open a breathing exercise" },
  { kind: "feather", side: "left", inset: 1, size: 22, delay: 14, duration: 36, tone: "var(--calm-lavender)", to: "/grounding", label: "Open a grounding technique" },
  { kind: "feather", side: "right", inset: 3.5, size: 20, delay: 24, duration: 40, tone: "var(--calm-silver)", to: "/affirmations", label: "Open positive affirmations" },
];

function Moon() {
  return <path d="M15.5 3.2a8.8 8.8 0 1 0 5.3 15.1A7.2 7.2 0 0 1 15.5 3.2z" fill="currentColor" />;
}

function Feather() {
  return (
    <>
      <path d="M19.5 3.5c-6 .4-11 4.9-11.6 11.2l-.4 3.8 3.8-.4c6.3-.6 10.8-5.6 11.2-11.6l.1-3.1-3.1.1z" fill="currentColor" />
      <path d="M17.5 6.5 4 20" stroke="var(--calm-quill)" strokeWidth="0.9" strokeLinecap="round" fill="none" />
    </>
  );
}

const PAUSE_KEY = "rt-calm-floaters-paused";

export function FloatingCalm() {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    setPaused(window.localStorage.getItem(PAUSE_KEY) === "1");
  }, []);
  const toggle = () => {
    setPaused((p) => {
      window.localStorage.setItem(PAUSE_KEY, p ? "0" : "1");
      return !p;
    });
  };

  return (
    <>
      <div className={`calm-floaters pointer-events-none fixed inset-0 z-20 overflow-hidden ${paused ? "is-paused" : ""}`}>
        {ITEMS.map((item, i) => (
          <Link
            key={i}
            to={item.to}
            aria-label={item.label}
            title={item.label}
            className={`calm-floater calm-floater--${item.kind} pointer-events-auto`}
            style={
              {
                [item.side]: `${item.inset}%`,
                top: item.kind === "moon" ? `${item.top}%` : undefined,
                width: item.size + 12,
                height: item.size + 12,
                color: item.tone,
                animationDelay: `-${item.delay}s`,
                animationDuration: `${item.duration}s`,
              } as React.CSSProperties
            }
          >
            <svg viewBox="0 0 24 24" width={item.size} height={item.size} aria-hidden>
              {item.kind === "moon" ? <Moon /> : <Feather />}
            </svg>
            <span className="calm-floater__sparkle" aria-hidden />
          </Link>
        ))}
      </div>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={paused}
        className="calm-floaters__toggle fixed bottom-3 left-3 z-20 inline-flex items-center gap-1 rounded-full border border-border bg-background/80 px-2.5 py-1 text-[11px] text-muted-foreground backdrop-blur hover:text-foreground"
      >
        {paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
        {paused ? "Play animations" : "Pause animations"}
      </button>
    </>
  );
}

export default FloatingCalm;
